import { ErrorModal } from 'components/ErrorModal';
import { isEmpty, map, partition } from 'es-toolkit/compat';
import { type AssetSearchResult, useCopyAssetMutation, useCreateAssetMutation } from 'generated/graphql';
import { type FC, useEffect, useState } from 'react';
import type { Observable } from 'rxjs';
import { ProgressModal } from '../../components/ProgressModal';
import {
  type AssetSelection,
  uploadFileToS3 as assetUtilUploadFileToS3,
  isExistingAssetSelection,
  isFileAssetSelection,
  measureAssetDimensions,
  type UploadProgress,
} from './assets.util';
import { ImageSelectorModal, type ImageSelectorModalProps } from './ImageSelectorModal';
import type { ImageTags } from './image-selector-modal.types';

export type AssetError = {
  assetErrorMessage: string;
};

export interface ImageSelectorModalContainerProps extends Omit<ImageSelectorModalProps, 'onInserted'> {
  // when all necessary uploading and/or copying of the asset selections the user made in the modal is complete
  onAssetsReady: (assets: AssetSearchResult[], errors: AssetError[]) => void;

  // a function that uploads a file to S3. This will default to the one in asset.utils. You can override that here,
  // which is useful for testing.
  uploadFileToS3?: (params: { file: File; signedUrl: string }) => Observable<UploadProgress>;
}

/**
 * This component wraps the ImageSelectorModal in logic that copies assets from other projects
 * to this project (from all projects) and creates new assets when necessary (from file system or MC).
 * It shows a progress indicator while these operations are in progress.
 */
export const ImageSelectorModalContainer: FC<ImageSelectorModalContainerProps> = (props) => {
  const { uploadFileToS3 = assetUtilUploadFileToS3 } = props;
  const [copyAsset, copyState] = useCopyAssetMutation();
  const [createAsset, createState] = useCreateAssetMutation();
  const [s3UploadProgress, setS3UploadProgress] = useState<UploadProgress>({
    done: true,
  });
  const [successfulAssets, setSuccessfulAssets] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const loading = copyState.loading || createState.loading || !s3UploadProgress.done;

  // if modal opened/closed, reset state
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    setErrors([]);
    setSuccessfulAssets([]);
    setS3UploadProgress({
      done: true,
    });
  }, [
    props.visible,
  ]);

  const onInserted = async (selections: AssetSelection[], tags: ImageTags) => {
    setErrors([]);
    setSuccessfulAssets([]);
    setS3UploadProgress({
      done: false,
    });

    const existingAssetSelections = selections.filter(isExistingAssetSelection);
    const [selectionsFromSameProject, selectionsFromOtherProjects] = partition(
      existingAssetSelections,
      (selection) => selection.asset.docId === props.projectId,
    );
    const { fileTypeFromBlob } = await import('file-type');

    // if from a different project, copy to this project
    const copiedAssets: Array<AssetSearchResult | AssetError> = await Promise.all(
      map(selectionsFromOtherProjects, async (selection) => {
        const copyResult = await copyAsset({
          variables: {
            assetId: selection.asset.assetId,
            destinationProjectId: props.projectId,
          },
        });

        if (!copyResult.data) {
          return {
            assetErrorMessage: copyState.error?.message ?? 'Could not copy asset',
          };
        }

        return copyResult.data?.copyAsset.asset;
      }),
    );

    const fileSelections = map(selections.filter(isFileAssetSelection), (s) => s.assetFile);
    const assetsToCreate = await Promise.all(
      map(fileSelections, async (file) => {
        const dimensions = await measureAssetDimensions(file).catch(() => ({
          height: null,
          width: null,
        }));
        return {
          dimensions,
          file,
        };
      }),
    );

    const createdAssets: Array<AssetSearchResult | AssetError> = await Promise.all(
      map(assetsToCreate, async ({ dimensions, file }) => {
        const fileType = await fileTypeFromBlob(file);
        const createdAsset = await createAsset({
          variables: {
            input: {
              device: tags.device,
              mediaType: fileType?.mime,
              metadata: dimensions
                ? {
                    height: dimensions.height,
                    width: dimensions.width,
                  }
                : {},
              name: file.name.substr(file.name.lastIndexOf('/') + 1),
              originalFileName: file.name,
              platform: tags.platform[0],
              productLine: tags.productLine,
              projectId: props.projectId,
              size: file.size,
              tags: tags.tags,
            },
          },
        });

        if (!createdAsset.data) {
          return {
            assetErrorMessage: createState.error?.message ?? 'Could not create asset',
          };
        }

        // upload to S3
        const uploadProgressObservable = uploadFileToS3({
          file,
          signedUrl: createdAsset.data.createAsset.asset.s3SignedUrlPut!,
        });

        try {
          await new Promise<void>((resolve, reject) => {
            uploadProgressObservable.subscribe({
              complete: () => resolve(),
              error: (err) => {
                reject(err);
              },
              next: (progress) => setS3UploadProgress(progress),
            });
          });
          // biome-ignore lint/suspicious/noExplicitAny: legacy code
        } catch (e: any) {
          return {
            assetErrorMessage: `Could not upload asset to S3: ${e.message}`,
          };
        }

        return createdAsset.data.createAsset.asset;
      }),
    );

    const readyAssetsOrErrors = [
      ...map(selectionsFromSameProject, (asset) => asset.asset),
      ...copiedAssets,
      ...createdAssets,
    ];

    const [errors, readyAssets] = partition(readyAssetsOrErrors, (obj) => 'assetErrorMessage' in obj) as [
      AssetError[],
      AssetSearchResult[],
    ];

    setErrors((prevErrors) => prevErrors.concat(map(errors, (e) => e.assetErrorMessage)));
    setSuccessfulAssets(map(readyAssets, (a) => a.name));
    setS3UploadProgress({
      done: true,
    });

    props.onAssetsReady(readyAssets, errors);
  };

  const hasErrors = !isEmpty(errors);
  const progressPercentage =
    s3UploadProgress.loaded !== undefined && s3UploadProgress.total !== undefined
      ? (s3UploadProgress.loaded / s3UploadProgress.total) * 100
      : 0;

  return (
    <>
      <ImageSelectorModal {...props} onInserted={onInserted} visible={props.visible && !loading && !hasErrors} />
      <ProgressModal
        loaderType="linear"
        message="Please do not navigate away, close, or refresh this browser window."
        open={props.visible && loading && !hasErrors}
        percentage={progressPercentage}
        title="Uploading media..."
      />
      <ErrorModal errors={errors} onClose={props.onHide} open={props.visible && hasErrors && !loading} successes={successfulAssets} />
    </>
  );
};
